import type { ComponentProps } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import * as React from 'react'
import { rem } from 'polished'
import { useIntl } from 'react-intl'
import LayoutContainer from 'components/Common/LayoutContainer/LayoutContainer'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import styled from 'styled-components'
import OfferListCarousel from 'components/OfferList/OfferListCarousel'
import CarouselViewAllLink from 'components/Common/CarouselViewAllLink'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import OfferCardCarouselSectionFilters from './OfferCardCarouselSectionFilters'
import {
  resolveOfferCardCarouselDestinationFilters,
  shouldResetOfferCardCarouselSelection,
} from './offerCardCarouselSectionUtils'
import useOfferListFilters from 'hooks/Offers/useOfferListFilters'
import { take, takeWhile } from 'lib/array/arrayUtils'
import { getHolidayTypeIcon } from 'lib/search/searchUtils'
import getOfferFeatureIcon from 'lib/offer/getOfferFeatureIcon'
import useTourFacetLocationCategory from 'hooks/TourV2/useTourFacetLocationCategory'
import Places from 'constants/places'
import BodyText from 'LuxKit/Typography/BodyText'
import { OFFER_TYPE_VILLA } from 'constants/offer'
import useOfferList from 'hooks/Offers/useOfferList'
import determineOfferFeatureSymbol from 'lib/offer/determineOfferFeatureSymbol'
import noop from 'lib/function/noop'
import useHomepageOfferCarouselList from 'hooks/Offers/useHomepageOfferCarouselList'
import useOfferListCarouselDisplayLimit from 'components/OfferList/useOfferListCarouselDisplayLimit'

export const OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS = 'offer-card-carousel-section--nested-in-trending'
export const MIN_OFFER_CAROUSEL_OFFERS_COUNT = 5
export const OFFER_CARD_CAROUSEL_SECTION_MOBILE_LIMIT = 8
export const OFFER_CARD_CAROUSEL_SECTION_TABLET_LIMIT = 'default' as const

const SectionContainer = styled(VerticalSpacer)`
  padding-block-start: ${rem(40)};
  padding-block-end: ${rem(40)};

  &.${OFFER_CARD_CAROUSEL_SECTION_NESTED_CLASS} {
    padding-block-start: 0;
    padding-block-end: 0;
  }
`

export type OfferCardCarouselSectionFilterChangeSource = 'user' | 'programmatic'

export type OfferCardCarouselSectionFilterChangeMeta = {
  source: OfferCardCarouselSectionFilterChangeSource
}

interface Props extends Utils.PropsWithDataAttributes, React.PropsWithChildren, Pick<ComponentProps<typeof OfferListCarousel>, 'tileStyle' | 'tileSize'> {
  /**
   * The title that'll be displayed
   */
  title: React.ReactNode
  /**
   * Sub title that'll be displayed below the title
   */
  subtitle?: React.ReactNode
  /**
   * The set of offer list filters for this carousel
   */
  filters: App.OfferListFilters
  /**
   * Add a line of filter chips for the type of filters given
   * Is able to auto fill holidayTypes, locations and bedrooms from filter data
   *
   * You *must* provide your own destinations through the 'additionalFilterOptions' prop
   */
  additionalFilters?: 'tourLocations' | 'holidayTypes' | 'locations' | 'bedrooms' | 'destinations' | 'amenities'
  /**
   * Provide your own set of filters for the above additional filter types
   */
  additionalFilterOptions?: Array<{ value: string, label: string }>
  /**
   * The number of filter chips to display. Defaults to five.
   */
  maxFilterOptions?: number
  /**
   * When provided, controls the selected additional filter chip.
   */
  selectedFilter?: string
  /**
   * The default filter that will be used when there is no filter set
   */
  defaultFilter?: string
  /**
   * The link the "view all" button will take the user to
   */
  allLink?: string
  /**
   * Optional builder used to derive the "view all" link based on the selected filter
   */
  buildAllLink?: (selectedFilter?: string) => string | undefined
  /**
   * Called whenever the selected additional filter changes
   */
  onSelectedFilterChange?: (
    selectedFilter?: string,
    meta?: OfferCardCarouselSectionFilterChangeMeta,
  ) => void
  /**
   * Exclude the following offer ids from the results
   */
  excludedIds?: Array<string>
  /**
   * Provide your own full offer list for the carousel to display, bypassing this component's own
   * fetch (based on `filters`) entirely — including for the hideWhenEmpty/minimumOffersCount check.
   */
  overrideOfferList?: App.OfferList
  id?: string
  /**
   * Hides the carousel when thre are no results for the filters given.
   *
   * NOTE: This is not ideal UX as it can cause the page to jump around as
   * the carousel loads it's data then disappears. Prefer other patterns
   * over this one (such as fall back values or "empty states"). This should
   * be a final resort only.
   */
  hideWhenEmpty?: boolean
  /**
   * The minimum number of offers that must be returned for the carousel to be displayed.
   *
   * Similar to "hideWhenEmpty" this should be used as a last resort only.
   */
  minimumOffersCount?: number
  /**
   * Reset the selected chip only when it is no longer present in additionalFilterOptions.
   */
  resetFilterWhenSelectionInvalid?: boolean
  /**
   * Feature-owned predicate for synthetic destination chips that should keep the
   * carousel's multi-place / stream base filters instead of narrowing to destinationId.
   */
  shouldKeepBaseDestinationFilters?: (selectedFilter: string) => boolean
  className?: string
}

const OfferCardCarouselSection = React.forwardRef<HTMLDivElement, Props>((props, ref) => {
  const {
    tileStyle = 'card-default',
    tileSize = 'small',
    title,
    subtitle,
    filters,
    additionalFilters,
    allLink,
    buildAllLink,
    onSelectedFilterChange = noop,
    children,
    additionalFilterOptions,
    maxFilterOptions = 5,
    selectedFilter: controlledSelectedFilter,
    defaultFilter,
    excludedIds,
    overrideOfferList,
    id,
    hideWhenEmpty,
    minimumOffersCount,
    resetFilterWhenSelectionInvalid,
    shouldKeepBaseDestinationFilters,
    className,
    ...rest
  } = props

  const intl = useIntl()
  const isSelectedFilterControlled = controlledSelectedFilter !== undefined
  const [uncontrolledSelectedFilter, setUncontrolledSelectedFilter] = useState<string | undefined>(defaultFilter)
  // A selected value can be supplied programmatically, so only a chip click should bypass the minimum-offers guard.
  const [hasManuallySelectedFilter, setHasManuallySelectedFilter] = useState(false)
  const selectedFilter = isSelectedFilterControlled ? controlledSelectedFilter : uncontrolledSelectedFilter
  const pendingFilterChangeRef = useRef<{
    value?: string
    source: OfferCardCarouselSectionFilterChangeSource
  } | undefined>(undefined)
  const isInitialSelectionSyncRef = useRef(true)
  const onSelectedFilterChangeRef = useRef(onSelectedFilterChange)
  const previousSelectedFilterRef = useRef<string | undefined>(undefined)

  onSelectedFilterChangeRef.current = onSelectedFilterChange

  const notifySelectedFilterChange = useCallback((
    value: string | undefined,
    source: OfferCardCarouselSectionFilterChangeSource,
  ) => {
    onSelectedFilterChangeRef.current(value, { source })
    isInitialSelectionSyncRef.current = false
    previousSelectedFilterRef.current = value
  }, [])

  useEffect(() => {
    if (!resetFilterWhenSelectionInvalid || !additionalFilterOptions?.length) {
      return
    }

    if (!shouldResetOfferCardCarouselSelection(selectedFilter, additionalFilterOptions)) {
      return
    }

    const nextFilter = defaultFilter
    if (nextFilter === selectedFilter) {
      return
    }

    setHasManuallySelectedFilter(false)

    if (isSelectedFilterControlled) {
      notifySelectedFilterChange(nextFilter, 'programmatic')
      return
    }

    pendingFilterChangeRef.current = { value: nextFilter, source: 'programmatic' }
    setUncontrolledSelectedFilter(nextFilter)
  }, [
    additionalFilterOptions,
    defaultFilter,
    isSelectedFilterControlled,
    notifySelectedFilterChange,
    resetFilterWhenSelectionInvalid,
    selectedFilter,
  ])

  useEffect(() => {
    if (isSelectedFilterControlled) {
      return
    }

    const pendingFilterChange = pendingFilterChangeRef.current
    if (pendingFilterChange && pendingFilterChange.value !== selectedFilter) {
      return
    }

    if (pendingFilterChange && pendingFilterChange.value === selectedFilter) {
      notifySelectedFilterChange(selectedFilter, pendingFilterChange.source)
      pendingFilterChangeRef.current = undefined
      return
    }

    const isInitialSync = isInitialSelectionSyncRef.current
    const hasSelectionChanged = previousSelectedFilterRef.current !== selectedFilter

    if (!isInitialSync && !hasSelectionChanged) {
      return
    }

    const source: OfferCardCarouselSectionFilterChangeSource = isInitialSync
      ? 'programmatic'
      : 'user'

    notifySelectedFilterChange(selectedFilter, source)
  }, [isSelectedFilterControlled, notifySelectedFilterChange, selectedFilter])

  useEffect(() => {
    if (!isSelectedFilterControlled || !isInitialSelectionSyncRef.current) {
      return
    }

    notifySelectedFilterChange(selectedFilter, 'programmatic')
  }, [isSelectedFilterControlled, notifySelectedFilterChange, selectedFilter])

  const toggleFilter = useCallback((nextFilter: string) => {
    const nextSelectedFilter = selectedFilter === nextFilter ? defaultFilter : nextFilter
    setHasManuallySelectedFilter(nextSelectedFilter !== defaultFilter)

    if (isSelectedFilterControlled) {
      notifySelectedFilterChange(nextSelectedFilter, 'user')
      return
    }

    pendingFilterChangeRef.current = { value: nextSelectedFilter, source: 'user' }
    setUncontrolledSelectedFilter(nextSelectedFilter)
  }, [defaultFilter, isSelectedFilterControlled, notifySelectedFilterChange, selectedFilter])

  const listFilters = useOfferListFilters(filters, { disabled: additionalFilters === 'tourLocations' })
  const tourFacets = useTourFacetLocationCategory({}, { disabled: additionalFilters !== 'tourLocations' })

  const filtersOptions = useMemo((): Array<{ label: string, value: string | number, icon?: React.ReactElement }> => {
    if (additionalFilterOptions) {
      return take(additionalFilterOptions, maxFilterOptions)
    }

    switch (additionalFilters) {
      case 'bedrooms':
        return take((listFilters.filters?.bedrooms ?? []).map((item) => ({
          label: intl.formatMessage({
            id: 'KjuYN7jOo-DIsApzKfRu',
            defaultMessage: '{count, plural, one {# bedroom} other {# bedrooms}}',
          }, { count: +item.slug }),
          value: +item.slug,
        })), maxFilterOptions)
      case 'holidayTypes':
        const holidayFilters = takeWhile(
          listFilters.orderedFilters?.holidayTypes ?? [],
          filter => filter.count >= 3,
          maxFilterOptions,
        )

        return holidayFilters.map((filter) => ({
          label: filter.label,
          value: filter.value,
          icon: getHolidayTypeIcon(filter.value),
        }))
      case 'destinations':
        // not automatically supported, use additionalFilterOptions to provide values
        return []
      case 'amenities':
        const amenityFilters = takeWhile(
          listFilters.orderedFilters?.amenities ?? [],
          filter => filter.count >= 3,
          maxFilterOptions,
        )
        return amenityFilters.map((filter) => {
          const Icon = getOfferFeatureIcon(determineOfferFeatureSymbol(filter.value))
          return {
            label: filter.label,
            value: filter.value,
            icon: <Icon />,
          }
        })
      case 'tourLocations':
        const tourLocationOptions = Array.from(tourFacets.locations.values()).map(value => ({
          label: value.name,
          value: value.id,
        }))

        const aroundTheWorldLabel = intl.formatMessage({
          id: 'cHfgYcUKNwHIOx1oDt2s',
          defaultMessage: 'Around the world',
        })

        return take([{
          label: aroundTheWorldLabel,
          value: Places.Anywhere.id,
        }].concat(tourLocationOptions),
        maxFilterOptions)
      default:
        return []
    }
  }, [additionalFilters, listFilters, additionalFilterOptions, tourFacets, maxFilterOptions, intl])

  const finalFilters = useMemo((): App.OfferListFilters => {
    switch (additionalFilters) {
      case 'bedrooms':
        // Only apply bedroomEq filters on homes and villas page. Requested by Jaime Benatar
        if (filters.offerTypes?.includes(OFFER_TYPE_VILLA)) {
          return {
            ...filters,
            bedroomsEq: selectedFilter ? +selectedFilter : undefined,
          }
        }
        return {
          ...filters,
          bedroomsGte: selectedFilter ? +selectedFilter : undefined,
        }
      case 'holidayTypes':
        return {
          ...filters,
          holidayTypes: selectedFilter ? [selectedFilter] : undefined,
        }
      case 'locations':
        return {
          ...filters,
          locations: selectedFilter ? [selectedFilter] : undefined,
        }
      case 'destinations':
      case 'tourLocations':
        return resolveOfferCardCarouselDestinationFilters(filters, selectedFilter, {
          shouldKeepBaseFilters: shouldKeepBaseDestinationFilters,
        })
      case 'amenities':
        return {
          ...filters,
          amenities: selectedFilter ? [selectedFilter] : undefined,
        }
      default:
        return filters
    }
  }, [additionalFilters, filters, selectedFilter, shouldKeepBaseDestinationFilters])

  // Keep the visibility check aligned with the list rendered by OfferListCarousel.
  // In particular, exclusions can otherwise leave a section with a heading and only
  // the "looking for something else" card.
  const fetchedList = useOfferList(finalFilters, {
    disabled: !!overrideOfferList,
    excludedIds,
  })
  const list = overrideOfferList ?? fetchedList
  const maxDisplayCount = useOfferListCarouselDisplayLimit(
    OFFER_CARD_CAROUSEL_SECTION_MOBILE_LIMIT,
    OFFER_CARD_CAROUSEL_SECTION_TABLET_LIMIT,
  )
  const resolvedAllLink = useMemo(() => {
    return buildAllLink?.(selectedFilter) ?? allLink
  }, [allLink, buildAllLink, selectedFilter])
  const display = useHomepageOfferCarouselList({
    offerList: list,
    minimumOffersCount,
    applyMinimumOffersCount: !!minimumOffersCount && !hasManuallySelectedFilter,
    hideWhenEmpty,
    maxDisplayCount,
  })

  if (display.hidden) {
    return null
  }

  return (
    <SectionContainer {...rest} className={className} gap={20} ref={ref} id={id}>
      <LayoutContainer>
        <VerticalSpacer gap={20}>
          <Group direction="horizontal" horizontalAlign="space-between" verticalAlign="start" gap={16}>
            <VerticalSpacer gap={8}>
              <Heading variant="heading2">{title}</Heading>
              {!!subtitle && <BodyText variant="large">{subtitle}</BodyText>}
            </VerticalSpacer>
            {!!resolvedAllLink && <CarouselViewAllLink to={resolvedAllLink}>
              {intl.formatMessage({
                id: 'K_ZTryhU0UJ8rG88VDpX',
                defaultMessage: 'View all',
              })}
            </CarouselViewAllLink>}
          </Group>
          {!!additionalFilters && <OfferCardCarouselSectionFilters
            options={filtersOptions}
            value={selectedFilter}
            onChange={toggleFilter}
          />}
        </VerticalSpacer>
      </LayoutContainer>
      <OfferListCarousel
        tileStyle={tileStyle}
        tileSize={tileSize}
        filters={finalFilters}
        overrideOfferList={display.isActive ? display.offerList : overrideOfferList}
        limit={OFFER_CARD_CAROUSEL_SECTION_MOBILE_LIMIT}
        tabletLimit={OFFER_CARD_CAROUSEL_SECTION_TABLET_LIMIT}
        excludedIds={excludedIds}
      >
        {children}
      </OfferListCarousel>
    </SectionContainer>
  )
})

OfferCardCarouselSection.displayName = 'OfferCardCarouselSection'

export default React.memo(OfferCardCarouselSection)
